/**
 * useMyDevices — 내 활성 기기 목록(D-3 기기관리). 현재 기기를 맨 앞에, 나머지는 최근 접속 순.
 * 설계: docs/design/12_통합관리_계정기기설계.md §8-D D-3.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./useAuth";
import { watchMyDevices } from "@/services/devices.repository";
import { activeDevicesOf } from "@/domain/device_info";
import type { DeviceDoc } from "@/domain/session";

export interface MyDevicesState {
  devices: DeviceDoc[];
  loading: boolean;
  error: Error | null;
  /** 이 기기 ID(공용 PC 로그인이면 null) */
  currentId: string | null;
}

export function useMyDevices(enabled = true): MyDevicesState {
  const { user, deviceId } = useAuth();
  const uid = user?.uid ?? null;
  const [raw, setRaw] = useState<DeviceDoc[]>([]);
  const [loadedUid, setLoadedUid] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uid || !enabled) return;
    return watchMyDevices(
      uid,
      (list) => {
        setRaw(list);
        setError(null);
        setLoadedUid(uid);
      },
      (e) => {
        setError(e);
        setLoadedUid(uid);
      },
    );
  }, [uid, enabled]);

  const devices = useMemo(() => {
    const act = activeDevicesOf(raw);
    const cur = act.find((d) => d.id === deviceId);
    return cur ? [cur, ...act.filter((d) => d.id !== deviceId)] : act;
  }, [raw, deviceId]);

  return { devices, loading: !!uid && enabled && loadedUid !== uid, error, currentId: deviceId };
}
